/**
 * StartWhatsAppSection - Seção para iniciar conversa WhatsApp a partir de outros canais
 * 
 * Permite adicionar número de telefone a contatos que vieram de canais como Instagram
 * e iniciar uma nova conversa via WhatsApp, mantendo o histórico unificado.
 * 
 * RELEASE: 2026-01-18
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, MessageSquare, Phone, Smartphone, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { normalizePhoneIdentifier, formatPhoneForDisplay } from '@/lib/phone-utils';
import { useQueryClient } from '@tanstack/react-query';
import { StartConversationDialog } from '@/components/tenant/contacts/StartConversationDialog';
import { Contact, Conversation } from '@/pages/tenant/Chat';
import { Json } from '@/integrations/supabase/types';

// Interface para props do componente
interface StartWhatsAppSectionProps {
  /** Dados do contato atual */
  contact: Contact;
  /** Conversa atual (para identificar canal) */
  conversation: Conversation;
  /** ID do tenant para validações e queries */
  tenantId: string;
  /** Callback após salvar telefone com sucesso */
  onPhoneSaved?: () => void;
}

/**
 * Verifica se é um número de telefone válido para WhatsApp
 * Após migration channel_ids, o campo phone só contém telefones reais
 * 
 * @param phone - Número de telefone a validar
 * @returns true se for um telefone válido para WhatsApp
 */
function isValidWhatsAppPhone(phone: string | null | undefined): boolean {
  if (!phone) return false;
  
  const digits = phone.replace(/\D/g, '');
  
  // Telefone válido: entre 10 e 15 dígitos
  return digits.length >= 10 && digits.length <= 15;
}

/**
 * Componente de seção para iniciar conversa WhatsApp
 * Exibe input de telefone ou botão de iniciar conversa
 */
export function StartWhatsAppSection({
  contact,
  conversation,
  tenantId,
  onPhoneSaved,
}: StartWhatsAppSectionProps) {
  // Estados locais
  const [phoneInput, setPhoneInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateContactName, setDuplicateContactName] = useState<string | null>(null);
  const [showConversationDialog, setShowConversationDialog] = useState(false);
  
  const queryClient = useQueryClient();
  
  // Verifica se contato já tem telefone válido para WhatsApp
  const hasValidPhone = isValidWhatsAppPhone(contact.phone);
  
  // Identificar canal de origem para mensagem contextual
  const channelType = conversation.inbox?.channel_type;
  const isInstagram = channelType === 'instagram';
  const isWebchat = channelType === 'webchat';
  
  /**
   * Verifica se telefone já existe em outro contato do tenant
   */
  const checkDuplicatePhone = async (normalizedPhone: string): Promise<{ isDuplicate: boolean; existingContactName: string | null }> => {
    try {
      const { data, error } = await supabase
        .from('contacts')
        .select('id, name')
        .eq('tenant_id', tenantId)
        .eq('phone', normalizedPhone)
        .neq('id', contact.id)
        .maybeSingle();
      
      if (error) throw error;
      
      return {
        isDuplicate: !!data,
        existingContactName: data?.name || null,
      };
    } catch (error) {
      console.error('[StartWhatsAppSection] Erro ao verificar duplicidade:', error);
      return { isDuplicate: false, existingContactName: null };
    }
  };
  
  /**
   * Salva o telefone no contato, preservando identificador original no metadata
   */
  const handleSavePhone = async () => {
    if (!phoneInput.trim()) {
      toast.error('Digite um número de telefone válido');
      return;
    }
    
    setIsSaving(true);
    setShowDuplicateWarning(false);
    
    try {
      // Normaliza o telefone
      const normalizedPhone = normalizePhoneIdentifier(phoneInput);
      
      if (!normalizedPhone || normalizedPhone.length < 10) {
        toast.error('Número de telefone inválido');
        setIsSaving(false);
        return;
      }
      
      // Verifica duplicidade
      const { isDuplicate, existingContactName } = await checkDuplicatePhone(normalizedPhone);
      
      if (isDuplicate) {
        setDuplicateContactName(existingContactName);
        setShowDuplicateWarning(true);
        setIsSaving(false);
        return;
      }
      
      // Prepara metadata 
      // NOTA: Após migration channel_ids, não precisamos mais preservar IGSID no metadata
      // pois o campo phone agora estará NULL para contatos Instagram (IGSID está em channel_ids)
      const currentMetadata = (contact.metadata || {}) as Record<string, Json>;
      const updatedMetadata: Record<string, Json> = {
        ...currentMetadata,
      };
      
      // Atualiza contato no banco
      const { error } = await supabase
        .from('contacts')
        .update({
          phone: normalizedPhone,
          metadata: updatedMetadata,
        })
        .eq('id', contact.id);
      
      if (error) throw error;
      
      toast.success('Telefone adicionado com sucesso!');
      
      // Invalida caches para refletir mudanças
      queryClient.invalidateQueries({ queryKey: ['contact-full-data', contact.id] });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      
      // Callback para componente pai
      onPhoneSaved?.();
      
      // Limpa input e abre dialog de iniciar conversa
      setPhoneInput('');
      setShowConversationDialog(true);
      
    } catch (error) {
      console.error('[StartWhatsAppSection] Erro ao salvar telefone:', error);
      toast.error('Erro ao salvar telefone. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };
  
  /**
   * Unifica contatos - Chama RPC merge_contacts para mesclar contato atual
   * (Instagram/Webchat) com contato existente que já tem o telefone
   */
  const handleForceSave = async () => {
    setShowDuplicateWarning(false);
    setIsSaving(true);
    
    const normalizedPhone = normalizePhoneIdentifier(phoneInput);
    
    try {
      // 1. Buscar ID do contato existente com esse telefone
      const { data: existingContact, error: findError } = await supabase
        .from('contacts')
        .select('id, name')
        .eq('tenant_id', tenantId)
        .eq('phone', normalizedPhone)
        .neq('id', contact.id)
        .single();
      
      if (findError || !existingContact) {
        console.error('[StartWhatsAppSection] Contato duplicado não encontrado:', findError);
        toast.error('Contato duplicado não encontrado');
        return;
      }
      
      // 2. Chamar RPC de merge: Contato atual (source) -> Contato existente (target)
      // O contato existente recebe todos os dados e relacionamentos
      const { data: mergeResult, error: mergeError } = await supabase.rpc(
        'merge_contacts',
        {
          p_source_contact_id: contact.id,        // Contato atual (Instagram/Webchat)
          p_target_contact_id: existingContact.id, // Contato com telefone (destino)
        }
      );
      
      if (mergeError) throw mergeError;
      
      // 3. Exibir resultado do merge
      const stats = (mergeResult as { merged_stats?: Record<string, number> })?.merged_stats || {};
      const targetId = (mergeResult as { target_contact_id?: string })?.target_contact_id;
      
      toast.success(
        `Contatos unificados! Migrados: ${stats.conversations || 0} conversas, ` +
        `${stats.deals || 0} negócios, ${stats.labels || 0} labels`
      );
      
      // 4. Invalidar caches para refletir mudanças
      queryClient.invalidateQueries({ queryKey: ['contact-full-data'] });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['crm-deals'] });
      
      // 5. Callback para componente pai (fechar sidebar ou navegar)
      onPhoneSaved?.();
      setPhoneInput('');
      
      // Nota: Não abrimos dialog de nova conversa porque o contato atual foi deletado
      // O usuário será redirecionado para o contato unificado
      
    } catch (error) {
      console.error('[StartWhatsAppSection] Erro ao unificar contatos:', error);
      toast.error('Erro ao unificar contatos. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Dados do contato para o dialog (com telefone atualizado)
  const contactForDialog = {
    id: contact.id,
    name: contact.name,
    phone: hasValidPhone ? contact.phone : normalizePhoneIdentifier(phoneInput) || null,
    email: contact.email,
    avatar_url: contact.avatar_url,
  };
  
  return (
    <>
      {/* Card da seção */}
      <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b bg-muted/30">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Iniciar Conversa WhatsApp</span>
          </div>
        </div>
        
        <div className="p-4 space-y-3">
          {hasValidPhone ? (
            // Contato já tem telefone válido - mostrar botão de iniciar
            <>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Smartphone className="h-4 w-4" />
                <span>{formatPhoneForDisplay(contact.phone)}</span>
              </div>
              <Button
                variant="default"
                size="sm"
                className="w-full bg-[#25D366] hover:bg-[#20BD5A] text-white"
                onClick={() => setShowConversationDialog(true)}
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Iniciar WhatsApp
              </Button>
            </>
          ) : (
            // Contato não tem telefone - mostrar input
            <>
              <p className="text-xs text-muted-foreground">
                {isInstagram 
                  ? 'Este contato veio do Instagram. Adicione o WhatsApp para continuar em outro canal.'
                  : isWebchat
                    ? 'Este contato veio do Webchat. Adicione o WhatsApp para continuar a conversa.'
                    : 'Adicione um número de telefone para iniciar conversa via WhatsApp.'
                }
              </p>
              
              <div className="space-y-2">
                <Label htmlFor="phone-input" className="text-xs">
                  Número de WhatsApp
                </Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="phone-input"
                      type="tel"
                      placeholder="+55 62 99999-9999"
                      value={phoneInput}
                      onChange={(e) => setPhoneInput(e.target.value)}
                      className="pl-9 h-9"
                      disabled={isSaving}
                    />
                  </div>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleSavePhone}
                    disabled={isSaving || !phoneInput.trim()}
                    className="h-9"
                  >
                    {isSaving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <MessageSquare className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Aviso de duplicidade - Oferece unificação de contatos */}
              {showDuplicateWarning && (
                <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <div className="text-xs text-amber-700 dark:text-amber-300">
                      <p className="font-medium">Telefone já cadastrado</p>
                      <p className="mt-1">
                        Este número pertence ao contato
                        {duplicateContactName ? ` "${duplicateContactName}"` : ' existente'}.
                        Deseja unificar os dois contatos?
                      </p>
                      <p className="mt-1 text-amber-600/80 dark:text-amber-400/80 text-[10px]">
                        Todas as conversas, negócios e histórico serão transferidos.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 h-7 text-xs"
                      onClick={() => setShowDuplicateWarning(false)}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      className="flex-1 h-7 text-xs"
                      onClick={handleForceSave}
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        'Unificar Contatos'
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Dialog para selecionar inbox e iniciar conversa */}
      <StartConversationDialog
        open={showConversationDialog}
        onOpenChange={setShowConversationDialog}
        contact={contactForDialog}
        tenantId={tenantId}
      />
    </>
  );
}
